import { MenuCard, MenuColumns, MenuFooter, type MenuColumn } from './topbar/menuCard'
import { MENU_CARD, PANEL_TOP, PANEL_TOP_MOBILE, Z } from './topbar/topbarChrome'

export type MegaMenuColumn = MenuColumn

/** The pages the marketing menu links to, each named by a label key. */
export type MarketingMenuKey =
  | 'theApp' | 'thisIsWehunt' | 'trackerWehunt' | 'subscriptionPlans' | 'maps' | 'integrations'
  | 'shop' | 'dogVests' | 'accessories' | 'dogTrackers'
  | 'aboutUs' | 'news' | 'thisIsTracker' | 'thisIsBurrel'

/**
 * Each page's address in every language wehunt.app publishes it in, taken from
 * the site's own sitemap (`https://www.wehunt.app/sitemap-index.xml`, the
 * hreflang alternates of each page). The site's slugs are translated per
 * language, so a page cannot be reached by swapping the locale prefix.
 *
 * A language the site does not publish a page in falls back to the English
 * page. Norwegian has no version of the site at all.
 *
 * This is a copy of the site's structure and drifts when the site moves a page:
 * the English links here had gone stale once already (`shop/…` became
 * `all-gear/…`, `about-us/…` became `read-more/…`). Regenerate it from the
 * sitemap rather than editing an address by hand.
 */
const MARKETING_PAGES: Record<Exclude<MarketingMenuKey, 'theApp' | 'shop' | 'aboutUs'>, Partial<Record<SiteLocale, string>> & { en: string }> = {
  thisIsWehunt: {
    en: 'https://wehunt.app/en/the-app/this-is-wehunt/',
    'sv-se': 'https://wehunt.app/sv-se/appen/detta-aer-wehunt/',
    'fi-fi': 'https://wehunt.app/fi-fi/tutustu/taemae-on-wehunt/',
    'de-de': 'https://wehunt.app/de-de/wissen/das-ist-wehunt/',
    'fr-fr': 'https://wehunt.app/fr-fr/apprendre/this-is-wehunt/',
    'da-dk': 'https://wehunt.app/da-dk/laering/dette-er-wehunt/',
  },
  trackerWehunt: {
    en: 'https://wehunt.app/en/the-app/tracker-wehunt-are-coming-together/',
    'sv-se': 'https://wehunt.app/sv-se/appen/tracker-wehunt-flyttar-ihop/',
    'fi-fi': 'https://wehunt.app/fi-fi/tutustu/tracker-ja-wehunt-yhdistyvaet/',
    'fr-fr': 'https://wehunt.app/fr-fr/apprendre/tracker-et-wehunt-se-rassemblent/',
    'da-dk': 'https://wehunt.app/da-dk/laering/tracker-wehunt-gaar-sammen/',
  },
  subscriptionPlans: {
    en: 'https://wehunt.app/en/the-app/subscription-plans/',
    'sv-se': 'https://wehunt.app/sv-se/appen/abonnemangsplaner/',
    'fi-fi': 'https://wehunt.app/fi-fi/tutustu/tilaustyypit/',
    'de-de': 'https://wehunt.app/de-de/wissen/abonnements/',
    'fr-fr': 'https://wehunt.app/fr-fr/apprendre/plans-dabonnement/',
    'da-dk': 'https://wehunt.app/da-dk/laering/abonnementsplaner/',
  },
  maps: {
    en: 'https://wehunt.app/en/maps-in-wehunt-1/',
    'sv-se': 'https://wehunt.app/sv-se/kartor-i-wehunt-1/',
    'fi-fi': 'https://wehunt.app/fi-fi/kartat-wehuntissa-1/',
    'fr-fr': 'https://wehunt.app/fr-fr/cartes-dans-wehunt-1/',
    'da-dk': 'https://wehunt.app/da-dk/kort-i-wehunt/',
  },
  integrations: {
    en: 'https://wehunt.app/en/the-app/integrations/',
    'sv-se': 'https://wehunt.app/sv-se/appen/integrationer/',
    'fi-fi': 'https://wehunt.app/fi-fi/tutustu/integraatiot/',
    'de-de': 'https://wehunt.app/de-de/wissen/integrationen/',
    'fr-fr': 'https://wehunt.app/fr-fr/apprendre/integrations/',
  },
  dogVests: {
    en: 'https://wehunt.app/en/all-gear/dog-vests/',
    'sv-se': 'https://wehunt.app/sv-se/all-utrustning/hundvaestar/',
    'fi-fi': 'https://wehunt.app/fi-fi/kaikki-varusteet/koiraliivit/',
    'de-de': 'https://wehunt.app/de-de/komplette-ausruestung/hundewesten/',
    'fr-fr': 'https://wehunt.app/fr-fr/tout-lequipement/harnais-de-chiens/',
    'da-dk': 'https://wehunt.app/da-dk/alt-udstyr/hundeveste/',
  },
  accessories: {
    en: 'https://wehunt.app/en/all-gear/accessories/',
    'sv-se': 'https://wehunt.app/sv-se/all-utrustning/tillbehoer/',
    'fi-fi': 'https://wehunt.app/fi-fi/kaikki-varusteet/oheistuotteet/',
    'de-de': 'https://wehunt.app/de-de/komplette-ausruestung/zubehoer/',
    'fr-fr': 'https://wehunt.app/fr-fr/tout-lequipement/accessoires/',
    'da-dk': 'https://wehunt.app/da-dk/alt-udstyr/tilbehoer/',
  },
  dogTrackers: {
    en: 'https://wehunt.app/en/all-gear/dog-tracking-devices/',
    'sv-se': 'https://wehunt.app/sv-se/all-utrustning/hundpejlar/',
    'fi-fi': 'https://wehunt.app/fi-fi/kaikki-varusteet/koiratutkat/',
    'de-de': 'https://wehunt.app/de-de/komplette-ausruestung/ortungsgeraete/',
    'fr-fr': 'https://wehunt.app/fr-fr/tout-lequipement/suivi-du-chien/',
    'da-dk': 'https://wehunt.app/da-dk/alt-udstyr/hundesporingsenheder/',
  },
  news: {
    en: 'https://wehunt.app/en/articles/',
    'sv-se': 'https://wehunt.app/sv-se/nyheter/',
    'fi-fi': 'https://wehunt.app/fi-fi/uutiset/',
    'de-de': 'https://wehunt.app/de-de/aktuelles/',
    'fr-fr': 'https://wehunt.app/fr-fr/actualites/',
    'da-dk': 'https://wehunt.app/da-dk/artikler/',
  },
  thisIsTracker: {
    en: 'https://wehunt.app/en/read-more/this-is-tracker/',
    'sv-se': 'https://wehunt.app/sv-se/laes-mer/det-haer-aer-tracker/',
    'fi-fi': 'https://wehunt.app/fi-fi/lue-lisaeae/taemae-on-tracker/',
  },
  thisIsBurrel: {
    en: 'https://wehunt.app/en/read-more/this-is-burrel/',
    'sv-se': 'https://wehunt.app/sv-se/laes-mer/det-haer-aer-burrel/',
  },
}

type SiteLocale = 'en' | 'sv-se' | 'fi-fi' | 'de-de' | 'fr-fr' | 'da-dk'
const SITE_LOCALE: Record<string, SiteLocale> = { sv: 'sv-se', fi: 'fi-fi', de: 'de-de', fr: 'fr-fr', da: 'da-dk' }

const MENU_TREE: { title: MarketingMenuKey; items: (keyof typeof MARKETING_PAGES)[] }[] = [
  { title: 'theApp', items: ['thisIsWehunt', 'trackerWehunt', 'subscriptionPlans', 'maps', 'integrations'] },
  { title: 'shop', items: ['dogVests', 'accessories', 'dogTrackers'] },
  { title: 'aboutUs', items: ['news', 'thisIsTracker', 'thisIsBurrel'] },
]

/**
 * The marketing menu in the UI language: labels from the caller (its own
 * locale files), each link to that language's page on wehunt.app.
 */
export function marketingMenuColumns(language: string, label: (key: MarketingMenuKey) => string): MegaMenuColumn[] {
  const site = SITE_LOCALE[language.toLowerCase().split('-')[0]] ?? 'en'
  return MENU_TREE.map(({ title, items }) => ({
    title: label(title),
    items: items.map((key) => ({ label: label(key), href: MARKETING_PAGES[key][site] ?? MARKETING_PAGES[key].en })),
  }))
}

const ENGLISH_LABELS: Record<MarketingMenuKey, string> = {
  theApp: 'The App',
  thisIsWehunt: 'This is WeHunt',
  trackerWehunt: 'Tracker & WeHunt are coming together',
  subscriptionPlans: 'Subscription Plans',
  maps: 'Maps in WeHunt',
  integrations: 'Integrations',
  shop: 'Shop',
  dogVests: 'Dog Vests',
  accessories: 'Accessories',
  dogTrackers: 'Dog Tracking Devices',
  aboutUs: 'About us',
  news: 'News',
  thisIsTracker: 'This is Tracker',
  thisIsBurrel: 'This is Burrel',
}

/**
 * The menu in English, for a shell that passes no localised tree. WeHunt Web
 * passes {@link marketingMenuColumns} in the UI language instead.
 */
export const DEFAULT_MEGA_MENU_COLUMNS: MegaMenuColumn[] = marketingMenuColumns('en', (key) => ENGLISH_LABELS[key])

export interface MegaMenuLabels {
  dialog?: string
  downloadTitle?: string
}

/**
 * The marketing menu the pill's toggle opens: wehunt.app's own card, dropped
 * under the bar over the shared backdrop, holding the link columns and the
 * app-store footer.
 *
 * The site re-renders a copy of the pill at the top of this panel and hides the
 * real one. Here the real pill stays put and is raised above the backdrop
 * instead, which lands the card at the same offset without putting a second
 * search/cart/account set in the accessibility tree.
 */
export function MegaMenu({
  onClose,
  columns = DEFAULT_MEGA_MENU_COLUMNS,
  labels,
  mobile = false,
  onNavigateTo,
}: {
  onClose: () => void
  columns?: MegaMenuColumn[]
  labels?: MegaMenuLabels
  mobile?: boolean
  /** See MenuColumns — routes in-app links instead of reloading. */
  onNavigateTo?: (href: string) => void
}) {
  const top = mobile ? PANEL_TOP_MOBILE : PANEL_TOP
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={labels?.dialog ?? 'Navigation menu'}
      style={{
        position: 'fixed',
        top,
        left: 0,
        right: 0,
        zIndex: Z.panel,
        display: 'flex',
        justifyContent: 'center',
        padding: mobile ? '0 16px' : '0 24px',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          width: '100%',
          maxWidth: MENU_CARD.maxWidth,
          maxHeight: `calc(100dvh - ${top + 24}px)`,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
        }}
      >
        <MenuCard mobile={mobile}>
          <MenuColumns columns={columns} onNavigate={onClose} onNavigateTo={onNavigateTo} />
          <MenuFooter title={labels?.downloadTitle} />
        </MenuCard>
      </div>
    </div>
  )
}
